import React, {forwardRef, useCallback, useId, useRef} from 'react';
import {useSearchField} from 'react-aria';
import {useSearchFieldState, type SearchFieldProps} from 'react-stately';

import IconSearch from '@spothero/icons/search';
import IconTimesCircleFilled from '@spothero/icons/x-circle-filled';
import {
    Button,
    Icon,
    InputGroup,
    InputLeftElement,
    InputRightElement,
} from '@spothero/ui';

import Input from 'common/form-elements/Input';

type StyleProps = Record<string, unknown>;

interface SearchInputProps {
    /** Additional props for the clear button */
    clearButtonProps?: StyleProps;
    /** data-testid for the input element */
    'data-testid'?: string;
    /** Default value for uncontrolled mode */
    defaultValue?: string;
    /** id for the input element */
    id?: string;
    /** Additional props for the InputGroup wrapper */
    inputGroupProps?: StyleProps;
    /** Additional props for the InputLeftElement wrapper */
    inputLeftElementProps?: StyleProps;
    /** Additional props spread onto the Input element */
    inputProps?: StyleProps;
    /** Whether the input is disabled */
    isDisabled?: boolean;
    /** Accessible label for the search field (required for accessibility, visually hidden by default) */
    label: string;
    /** Called when the input value changes (receives string value, not event) */
    onChange?: SearchFieldProps['onChange'];
    /** Called when the search is cleared */
    onClear?: SearchFieldProps['onClear'];
    /** Called when the search is submitted (enables the Submit button) */
    onSubmit?: SearchFieldProps['onSubmit'];
    /** Placeholder text for the input */
    placeholder?: string;
    /** Additional props for the search icon in InputLeftElement */
    searchIconProps?: StyleProps;
    /** Whether to show the clear button when input has a value. Defaults to true. */
    showClearButton?: boolean;
    /** Whether to show the Submit button. Defaults to true when onSubmit is provided. */
    showSubmitButton?: boolean;
    /** Label text for the Submit button. Defaults to "Search". */
    submitButtonLabel?: string;
    /** Additional props for the Submit button */
    submitButtonProps?: StyleProps;
    /** Controlled value */
    value?: string;
}

const defaultSearchIconProps = {
    color: 'black',
    fontSize: 'sm',
    boxSize: 4.5,
};

const defaultInputStyleProps = (showSubmit: boolean, showClear: boolean) =>
    ({
        backgroundColor: 'white',
        paddingLeft: 10,
        paddingRight: showSubmit ? 32 : showClear ? 10 : 2,
    }) as const;

const defaultClearButtonProps = {
    variant: 'tertiary' as const,
    color: 'gray.dark',
    _hover: {
        color: 'gray.500',
    },
} as const;

const defaultClearIconProps = {
    boxSize: 4.5,
};

const defaultSubmitClearButtonProps = {
    variant: 'tertiary' as const,
    width: 10,
    height: 10,
    borderTopRightRadius: 'none',
    borderBottomRightRadius: 'none',
    borderTopLeftRadius: 'md',
    borderBottomLeftRadius: 'md',
    iconSpacing: 0,
    color: 'gray.dark',
    _hover: {
        color: 'gray.500',
    },
} as const;

const defaultSubmitButtonProps = {
    width: 24,
    height: 10,
    paddingX: 3,
    borderTopRightRadius: 'md',
    borderBottomRightRadius: 'md',
    borderTopLeftRadius: 'none',
    borderBottomLeftRadius: 'none',
} as const;

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    (
        {
            label,
            placeholder,
            defaultValue,
            value,
            onChange,
            onSubmit,
            onClear,
            showClearButton = true,
            showSubmitButton,
            submitButtonLabel = 'Search',
            isDisabled,
            inputGroupProps,
            inputProps,
            searchIconProps,
            inputLeftElementProps,
            clearButtonProps,
            submitButtonProps,
            'data-testid': dataTestId,
            id,
        },
        externalRef
    ) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const internalRef = useRef<HTMLInputElement | null>(null);

        const setRef = useCallback(
            (node: HTMLInputElement | null) => {
                internalRef.current = node;

                if (typeof externalRef === 'function') {
                    externalRef(node);
                } else if (externalRef) {
                    externalRef.current = node;
                }
            },
            [externalRef]
        );

        const stateProps = {
            'aria-label': label,
            defaultValue,
            value,
            onChange,
            onSubmit,
            onClear,
            isDisabled,
        };

        const state = useSearchFieldState(stateProps);
        const {inputProps: ariaInputProps} = useSearchField(
            stateProps,
            state,
            internalRef
        );

        const hasSubmitButton = showSubmitButton ?? Boolean(onSubmit);
        const hasValue = Boolean(state.value);

        const handleClear = useCallback(() => {
            state.setValue('');
            onClear?.();
            internalRef.current?.focus();
        }, [state, onClear]);

        const handleSubmit = useCallback(() => {
            onSubmit?.(state.value);
        }, [onSubmit, state]);

        const showClear = showClearButton && hasValue;
        const showSubmit = hasSubmitButton && hasValue;
        const showRightElement = showClear || showSubmit;

        return (
            <InputGroup width="100%" {...inputGroupProps}>
                <InputLeftElement
                    pointerEvents="none"
                    width={8.5}
                    {...inputLeftElementProps}
                >
                    <Icon
                        as={IconSearch}
                        {...defaultSearchIconProps}
                        {...searchIconProps}
                    />
                </InputLeftElement>
                <Input
                    ref={setRef}
                    {...ariaInputProps}
                    id={inputId}
                    placeholder={placeholder}
                    includeFormControl={false}
                    data-testid={dataTestId}
                    {...defaultInputStyleProps(showSubmit, showClear)}
                    {...inputProps}
                />
                {showRightElement && (
                    <InputRightElement
                        {...(hasSubmitButton ? {width: 32} : {})}
                    >
                        {showClear && (
                            <Button
                                onClick={handleClear}
                                aria-label="clear search"
                                title="Clear"
                                {...(hasSubmitButton
                                    ? {
                                          ...defaultSubmitClearButtonProps,
                                          leftIcon: (
                                              <Icon
                                                  as={IconTimesCircleFilled}
                                                  {...defaultClearIconProps}
                                              />
                                          ),
                                      }
                                    : defaultClearButtonProps)}
                                {...clearButtonProps}
                            >
                                {!hasSubmitButton && (
                                    <Icon
                                        as={IconTimesCircleFilled}
                                        {...defaultClearIconProps}
                                    />
                                )}
                            </Button>
                        )}
                        {hasSubmitButton && hasValue && (
                            <Button
                                onClick={handleSubmit}
                                aria-label="search"
                                title="Search"
                                {...defaultSubmitButtonProps}
                                {...submitButtonProps}
                            >
                                {submitButtonLabel}
                            </Button>
                        )}
                    </InputRightElement>
                )}
            </InputGroup>
        );
    }
);

export default SearchInput;
